import React, { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { experienceSchema } from "./EasyApplyValidationSchema";
import { useFormikContext } from "formik";
import { Plus } from "lucide-react";
import { getInitialExperienceState } from "../../constant/VonqConstant";
import Label from "../Common/Label";
import Button from "@mui/material/Button";

// Type definitions for experience
export interface ExperienceEntry {
  years: any;
  role: any;
  id: string;
  title: string;
  company: string;
  location: string;
  description: string;
  currentlyWorking: boolean;
  startDate: string;
  endDate: string | null;
}

export interface ExperienceErrors {
  title?: string;
  company?: string;
  location?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  currentlyWorking?: string;
}

interface ExperienceProps {
  experiences: ExperienceEntry[];
}

const Experience: React.FC<ExperienceProps> = ({ experiences }) => {
  const { setFieldValue, errors, touched } = useFormikContext<{
    experience: ExperienceEntry[];
  }>();

  const [isExperienceFormVisible, setIsExperienceFormVisible] =
    useState<boolean>(false);
  const [editingExperienceId, setEditingExperienceId] = useState<string | null>(
    null
  );
  const [experienceFormErrors, setExperienceFormErrors] =
    useState<ExperienceErrors>({});
  const [experienceDraft, setExperienceDraft] = useState<ExperienceEntry>({
    id: "",
    title: "",
    company: "",
    location: "",
    description: "",
    currentlyWorking: false,
    startDate: "",
    endDate: "",
    years: "",
    role: "",
  });

  const handleAdd = () => {
    setExperienceDraft(getInitialExperienceState());
    setEditingExperienceId(null);
    setIsExperienceFormVisible(true);
    setExperienceFormErrors({});
  };

  const handleEdit = (experience: ExperienceEntry) => {
    setExperienceDraft(experience);
    setEditingExperienceId(experience.id);
    setIsExperienceFormVisible(true);
    setExperienceFormErrors({});
  };

  const validateExperience = async (
    experience: ExperienceEntry
  ): Promise<boolean> => {
    try {
      await experienceSchema.validate(experience, { abortEarly: false });
      setExperienceFormErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: ExperienceErrors = {};
      error.inner.forEach((err: any) => {
        validationErrors[err.path as keyof ExperienceErrors] = err.message;
      });
      setExperienceFormErrors(validationErrors);
      return false;
    }
  };

  const handleSave = async () => {
    const isValid = await validateExperience(experienceDraft);
    if (!isValid) return;

    let updatedExperiences: ExperienceEntry[];

    if (editingExperienceId) {
      updatedExperiences = experiences.map((exp) =>
        exp.id === editingExperienceId ? experienceDraft : exp
      );
    } else {
      updatedExperiences = [...experiences, { ...experienceDraft }];
    }

    setFieldValue("experience", updatedExperiences);
    setIsExperienceFormVisible(false);
    setEditingExperienceId(null);
    setExperienceFormErrors({});
  };

  const handleCancel = () => {
    setIsExperienceFormVisible(false);
    setEditingExperienceId(null);
    setExperienceFormErrors({});
    setExperienceDraft({
      id: "",
      title: "",
      company: "",
      location: "",
      description: "",
      currentlyWorking: false,
      startDate: "",
      endDate: "",
      years: "",
      role: "",
    });
  };

  const handleDelete = (id: string) => {
    const updatedExperiences = experiences.filter((exp) => exp.id !== id);
    setFieldValue("experience", updatedExperiences);
  };

  const handleTempChange = (
    field: keyof ExperienceEntry,
    value: string | boolean
  ) => {
    setExperienceDraft((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "currentlyWorking" && value ? { endDate: null } : {}),
    }));

    if (experienceFormErrors[field as keyof ExperienceErrors]) {
      setExperienceFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const getFieldError = (fieldName: keyof ExperienceErrors) => {
    return experienceFormErrors[fieldName];
  };

  const isFieldInvalid = (fieldName: keyof ExperienceErrors) => {
    return !!experienceFormErrors[fieldName];
  };

  return (
    <div className="form-section">
      <div className="d-flex justify-content-between align-items-center mb-4">
        {/* <h4 className="section-title mb-0">Experience</h4> */}
        <Label
          children="Experience"
          className="section-title mb-0"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
          }}
        />

        <button
          type="button"
          className="pill-btn pill-btn--accent d-flex align-items-center gap-2"
          onClick={handleAdd}
        >
          <Plus size={18} />
          Add experience
        </button>
      </div>

      {/* Display existing experience entries */}
      {experiences.map((exp, index) => (
        <div key={exp.id} className="mb-4 p-3 border rounded bg-light">
          <div className="d-flex align-items-start gap-3">
            <div className="experience-icon"></div>
            <div className="flex-grow-1">
              {/* <h6 className="mb-1">{exp.title}</h6> */}

              <Label children={exp.company} className="mb-1" />

              {/* <p className="text-muted mb-1">{exp.company}</p> */}

              <Label children={exp.company} className="text-muted mb-1" />
              <Label className="text-muted mb-0">
                {exp.startDate} -{" "}
                {exp.currentlyWorking ? "Present" : exp.endDate}
              </Label>

              {errors.experience?.[index] && touched.experience?.[index] && (
                <div className="error-message mt-2">
                  {Object.values(errors.experience[index]).join(", ")}
                </div>
              )}
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <button
                type="button"
                className="pill-btn pill-btn--ghost"
                onClick={() => handleEdit(exp)}
              >
                Edit
              </button>
              <button
                type="button"
                className="pill-btn pill-btn--danger"
                onClick={() => handleDelete(exp.id)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Experience Form */}
      {isExperienceFormVisible && (
        <div className="mb-4">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">
                Title <span className="required-field">*</span>
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className={`form-control ${
                    isFieldInvalid("title") ? "is-invalid" : ""
                  }`}
                  value={experienceDraft.title}
                  onChange={(e) => handleTempChange("title", e.target.value)}
                  placeholder="Enter Title"
                />
                <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                </div>
              </div>
              {getFieldError("title") && (
                <div className="error-message">{getFieldError("title")}</div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Company <span className="required-field">*</span>
              </label>
              <div className="position-relative">
                <input
                  type="text"
                  className={`form-control ${
                    isFieldInvalid("company") ? "is-invalid" : ""
                  }`}
                  value={experienceDraft.company}
                  onChange={(e) => handleTempChange("company", e.target.value)}
                  placeholder="Enter Company Name"
                />
                <div className="position-absolute end-0 top-50 translate-middle-y me-2"></div>
              </div>
              {getFieldError("company") && (
                <div className="error-message">{getFieldError("company")}</div>
              )}
            </div>

            <div className="col-12">
              <label className="form-label">Office location</label>
              <div className="position-relative">
                <input
                  type="text"
                  className={`form-control ${
                    isFieldInvalid("location") ? "is-invalid" : ""
                  }`}
                  placeholder="Enter Location"
                  value={experienceDraft.location}
                  onChange={(e) => handleTempChange("location", e.target.value)}
                />
                <div className="position-absolute end-0 top-50 translate-middle-y me-2"></div>
              </div>
              {getFieldError("location") && (
                <div className="error-message">{getFieldError("location")}</div>
              )}
            </div>

            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className={`form-control ${
                  isFieldInvalid("description") ? "is-invalid" : ""
                }`}
                rows={4}
                value={experienceDraft.description}
                onChange={(e) =>
                  handleTempChange("description", e.target.value)
                }
                placeholder="Write here..."
              />
              {getFieldError("description") && (
                <div className="error-message">
                  {getFieldError("description")}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                From <span className="required-field">*</span>
              </label>
              <div
                className={`date-picker-shell ${
                  isFieldInvalid("startDate") ? "has-error" : ""
                }`}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      experienceDraft.startDate
                        ? dayjs(experienceDraft.startDate)
                        : null
                    }
                    onChange={(newValue: Dayjs | null) => {
                      const formatted = newValue
                        ? newValue.format("YYYY-MM-DD")
                        : "";
                      handleTempChange("startDate", formatted);
                    }}
                    disableFuture
                    minDate={dayjs("1900-01-01")}
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "Pick a date",
                        variant: "outlined",
                        className: "date-picker-input",
                        size: "small",
                        fullWidth: true,
                        InputLabelProps: { shrink: false },
                        inputProps: {
                          className: "date-picker-input-field",
                          placeholder: "Pick a date",
                        },
                      } as any,
                      openPickerIcon: {
                        className: "date-picker-icon",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
              {getFieldError("startDate") && (
                <div className="error-message">
                  {getFieldError("startDate")}
                </div>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">
                To <span className="required-field">*</span>
              </label>
              <div
                className={`date-picker-shell ${
                  isFieldInvalid("endDate") ? "has-error" : ""
                }`}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={
                      experienceDraft.endDate
                        ? dayjs(experienceDraft.endDate)
                        : null
                    }
                    onChange={(newValue: Dayjs | null) => {
                      const formatted = newValue
                        ? newValue.format("YYYY-MM-DD")
                        : "";
                      handleTempChange("endDate", formatted);
                    }}
                    disabled={experienceDraft.currentlyWorking}
                    minDate={
                      experienceDraft.startDate
                        ? dayjs(experienceDraft.startDate)
                        : dayjs("1900-01-01")
                    }
                    disableFuture
                    format="MM/DD/YYYY"
                    slotProps={{
                      textField: {
                        placeholder: "Pick a date",
                        variant: "outlined",
                        className: "date-picker-input",
                        size: "small",
                        fullWidth: true,
                        InputLabelProps: { shrink: false },
                        inputProps: {
                          className: "date-picker-input-field",
                          placeholder: "Pick a date",
                        },
                      } as any,
                      openPickerIcon: {
                        className: "date-picker-icon",
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
              {getFieldError("endDate") && (
                <div className="error-message">{getFieldError("endDate")}</div>
              )}
            </div>

            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={experienceDraft.currentlyWorking}
                  onChange={(e) =>
                    handleTempChange("currentlyWorking", e.target.checked)
                  }
                  id="currentlyWorking"
                />
                <label className="form-check-label" htmlFor="currentlyWorking">
                  I currently work here
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mt-3 justify-content-end">
            <button
              type="button"
              className="pill-btn pill-btn--ghost"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="pill-btn pill-btn--primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Experience;
